import { Skeleton } from "@organizacaox/lai-design-system";
import "./course-catalog.css";

export type CourseCatalogSkeletonLayout = "catalog" | "filtered" | "search";

function PosterSkeleton({ index }: { index: number }) {
  return (
    <div className="relative aspect-video w-full min-w-0 overflow-hidden rounded-xl border bg-card">
      <Skeleton className="absolute inset-0 size-full rounded-none opacity-40" />
      <div className="absolute inset-x-4 bottom-4 flex min-w-0 flex-col gap-3">
        <Skeleton className={`h-4 ${index % 2 ? "w-2/3" : "w-4/5"}`} />
        <div className="flex gap-3">
          <Skeleton className="h-3 w-14" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    </div>
  );
}

function ShelfSkeleton({ heading = false }: { heading?: boolean }) {
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex min-w-0 items-center justify-between gap-3">
        {heading && <Skeleton className="h-6 w-44 max-w-full" />}
        <div className="ml-auto flex shrink-0 gap-2">
          <Skeleton variant="circle" className="size-8" />
          <Skeleton variant="circle" className="size-8" />
        </div>
      </div>
      <div className="overflow-hidden">
        <div className="-ml-3 flex">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="min-w-0 shrink-0 basis-[94%] pl-3 sm:basis-[70%] lg:basis-1/3"
            >
              <PosterSkeleton index={index} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterSkeletons() {
  return (
    <div className="course-catalog-filters">
      {["w-16", "w-28", "w-24", "w-24"].map((width, index) => (
        <Skeleton key={index} className={`h-8 rounded-full ${width}`} />
      ))}
    </div>
  );
}

export function CourseCatalogSkeleton({
  layout = "catalog",
  includeToolbar = false,
  label = layout === "search" ? "Carregando pesquisa de cursos…" : "Carregando cursos…",
}: {
  layout?: CourseCatalogSkeletonLayout;
  includeToolbar?: boolean;
  label?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-course-catalog-skeleton={layout}
      className="course-catalog-skeleton w-full min-w-0 motion-reduce:[&_[data-slot=skeleton]]:animate-none"
    >
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="flex min-w-0 flex-col gap-6">
        {includeToolbar &&
          (layout === "search" ? (
            <div className="space-y-5">
              <Skeleton className="h-8 w-36" />
              <div className="space-y-3">
                <Skeleton className="h-9 w-72 max-w-[90%]" />
                <Skeleton className="h-4 w-64 max-w-[80%]" />
              </div>
              <div className="flex h-14 items-center gap-3 rounded-md border bg-card px-4">
                <Skeleton variant="circle" className="size-5 shrink-0" />
                <Skeleton className="h-4 w-1/3" />
              </div>
              <FilterSkeletons />
            </div>
          ) : (
            <div className="course-catalog-toolbar">
              <Skeleton className="course-catalog-title h-9 w-28" />
              <FilterSkeletons />
              <Skeleton variant="circle" className="course-catalog-search size-11" />
            </div>
          ))}
        {layout === "catalog" && (
          <div className="course-feature course-catalog-skeleton-feature border bg-card">
            <Skeleton className="absolute inset-0 size-full rounded-none opacity-30" />
            <div className="relative flex h-full min-h-[inherit] flex-col justify-end gap-5 p-5 sm:p-8 lg:p-12">
              <Skeleton className="h-4 w-28" />
              <div className="flex w-full max-w-xl flex-col gap-3">
                <Skeleton className="h-8 w-full sm:h-10" />
                <Skeleton className="h-8 w-2/3 sm:h-10" />
              </div>
              <div className="flex flex-wrap gap-4">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-2 w-2/3 max-w-sm rounded-full" />
              <div className="flex flex-wrap gap-3">
                <Skeleton className="h-10 w-40 max-w-full rounded-lg" />
                <Skeleton className="h-10 w-28 rounded-lg" />
              </div>
            </div>
          </div>
        )}
        {layout === "search" ? (
          <div className="space-y-5">
            <Skeleton className="h-4 w-40" />
            <div className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <PosterSkeleton key={index} index={index} />
              ))}
            </div>
          </div>
        ) : (
          <>
            <ShelfSkeleton />
            {layout === "catalog" && (
              <>
                <ShelfSkeleton heading />
                <ShelfSkeleton heading />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
